import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router';
import { normalizeUsername, type SlotInfo } from '../../shared/events';
import { Chevron, Globe, Logo, Speaker } from '../components/Icons';
import { APP_LANGS, fmt, useT } from '../lib/i18n';
import { session } from '../lib/session';
import { addRecent, getRecent, updateSettings, useSettings } from '../lib/settings';
import { unlockAudio } from '../lib/sfx';
import { EdgeHint } from '../components/Voice';
import { VOICE_LANGS } from '../lib/languages';

export function Start() {
  const t = useT();
  const s = useSettings();
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [slots, setSlots] = useState<SlotInfo | null>(null);
  const [slotsFailed, setSlotsFailed] = useState(false);
  const recent = getRecent();

  useEffect(() => {
    session.stop();
    let alive = true;
    const load = () =>
      fetch('/api/slots')
        .then((r) => r.json() as Promise<SlotInfo>)
        .then((d) => alive && setSlots(d))
        .catch(() => alive && setSlotsFailed(true));
    void load();
    // A tab left open across deployments picks up the new build here, never mid-stream.
    const myBuild = typeof __BUILD_ID__ === 'string' ? __BUILD_ID__ : 'dev';
    fetch('/api/health', { cache: 'no-store' })
      .then((r) => r.json() as Promise<{ build?: string }>)
      .then((d) => {
        if (!alive || !d.build || myBuild === 'dev' || d.build === myBuild) return;
        // Reload at most once per server build: if the reloaded page still disagrees (a cached
        // bundle, or a build whose two halves got different ids), stay put instead of looping.
        let already = false;
        try {
          already = sessionStorage.getItem('bacachat:reloadedFor') === d.build;
          sessionStorage.setItem('bacachat:reloadedFor', d.build);
        } catch {
          already = true; // storage blocked: cannot guard against a loop, so never auto-reload
        }
        if (!already) location.reload();
      })
      .catch(() => {});
    const id = window.setInterval(load, 30_000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  const go = (raw: string) => {
    const u = normalizeUsername(raw);
    if (!u) {
      setError(t.invalidUsername);
      return;
    }
    unlockAudio();
    addRecent(u);
    nav(`/live/${u}`);
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    go(username);
  };

  const nextLang = () => {
    const i = APP_LANGS.findIndex((l) => l.value === s.appLang);
    const next = APP_LANGS[(i + 1) % APP_LANGS.length].value;
    updateSettings({ appLang: next });
  };

  const voiceLabel = VOICE_LANGS.find((v) => v.value === s.voiceLang)?.label ?? s.voiceLang;
  const free = slots ? Math.max(0, slots.max - slots.used) : null;

  return (
    <div className="screen">
      <header className="topbar">
        <Link to="/" className="brand">
          <Logo />
          <span className="brand__name">BacaChat</span>
        </Link>
        <button type="button" className="chip" onClick={nextLang} aria-label={t.appLanguage}>
          <Globe />
          {s.appLang.toUpperCase()}
        </button>
      </header>

      <section className="hero" style={{ paddingTop: 12 }}>
        <h1>{t.tagline}</h1>
        <p>{t.intro}</p>
      </section>

      <form className="stack" onSubmit={submit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <label htmlFor="un" className="label">
            {t.usernameLabel}
          </label>
          <span className={`slotbadge${free === 0 ? ' slotbadge--full' : ''}`} aria-live="polite">
            <span className="slotbadge__dot" />
            {slots ? (free === 0 ? fmt(t.slotsNone, { max: slots.max }) : fmt(t.slotsFree, { n: free ?? 0, max: slots.max })) : slotsFailed ? '—' : t.slotsLoading}
          </span>
        </div>
        <div className="field">
          <span className="field__at">@</span>
          <input
            id="un"
            type="text"
            placeholder={t.usernamePlaceholder}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            value={username}
            onChange={(e) => {
              setUsername(e.target.value);
              setError('');
            }}
            aria-invalid={Boolean(error)}
            aria-describedby="un-hint"
          />
        </div>
        <button type="submit" className="btn btn--primary">
          <Speaker />
          {t.start}
        </button>
        <p id="un-hint" className="hint" style={error ? { color: 'var(--accent-light)' } : undefined}>
          {error || t.mustBeLive}
        </p>
      </form>

      <section className="card">
        <Link to="/settings" className="row">
          <span>
            <span className="row__title">{t.voiceLanguage}</span>
            <span className="row__sub">{voiceLabel}</span>
          </span>
          <Chevron style={{ color: 'var(--dim)' }} />
        </Link>
        <EdgeHint compact />
        <label className="row">
          <span className="row__title">{t.readAloud}</span>
          <input className="sw" type="checkbox" role="switch" checked={s.voiceOn} onChange={(e) => updateSettings({ voiceOn: e.target.checked })} />
        </label>
        <label className="row">
          <span className="row__title">{t.soundFor}</span>
          <input className="sw" type="checkbox" role="switch" checked={s.sfxOn} onChange={(e) => updateSettings({ sfxOn: e.target.checked })} />
        </label>
      </section>

      {recent.length > 0 && (
        <section className="stack" style={{ gap: 10 }}>
          <h2 className="h2">{t.recent}</h2>
          <div className="chips">
            {recent.map((u) => (
              <button key={u} type="button" className="chip" onClick={() => go(u)}>
                @{u}
              </button>
            ))}
          </div>
        </section>
      )}

      <footer className="footer">
        <span className="footer__motto">Gendong Odette sampai Immo</span>
        <span>bacachat.com</span>
      </footer>
    </div>
  );
}
